import { Configuration } from '@/types/api';
import api from '@/lib/api-client';
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback, useContext } from 'react';
import { SnackBarContext } from 'pragmatic-ui';
import {
  CreateConfigurationPayload,
  UpdateConfigurationPayload,
} from './usePreSignedUrls';

const configurationsKey = ['configurations'];

async function getConfigurations(): Promise<Configuration[]> {
  const response = await api.get(`/v1/admin/configurations`);
  return response.data;
}

export const useConfigurations = (
  options?: Omit<
    UseQueryOptions<Configuration[], Error, Configuration[], QueryKey>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    queryKey: configurationsKey,
    queryFn: getConfigurations,
    ...options,
  });

async function getConfiguration(id: string): Promise<Configuration> {
  const response = await api.get(`/v1/admin/configurations/${id}`);
  return response.data;
}

export const useConfiguration = (
  id?: string,
  options?: Omit<
    UseQueryOptions<Configuration, Error, Configuration, QueryKey>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    queryKey: [...configurationsKey, id],
    enabled: !!id,
    queryFn: () => getConfiguration(id as string),
    ...options,
  });

async function createConfiguration(
  payload: CreateConfigurationPayload,
): Promise<Configuration> {
  const response = (await api.post('/v1/admin/configurations', payload)) as any;
  return response.configuration;
}

export const useCreateConfiguration = (
  options?: Omit<
    UseMutationOptions<Configuration, Error, CreateConfigurationPayload>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  const { showSnack } = useContext(SnackBarContext);

  const setConfiguration = useCallback(
    (newConfiguration: Configuration) => {
      queryClient.setQueryData<Configuration[]>(
        configurationsKey,
        (oldData) => {
          return oldData ? [...oldData, newConfiguration] : [newConfiguration];
        },
      );
    },
    [queryClient],
  );

  return useMutation({
    mutationFn: createConfiguration,
    ...options,
    onSuccess: (configuration, ...rest) => {
      setConfiguration(configuration);
      queryClient.invalidateQueries({ queryKey: configurationsKey });
      options?.onSuccess?.(configuration, ...rest);
      showSnack('Successfully added new configuration', 'success');
    },
    onError: () => {
      showSnack('There was an unexpected error, please try again', 'error');
    },
  });
};

async function updateConfiguration(
  payload: UpdateConfigurationPayload,
): Promise<Configuration> {
  const { _id, ...body } = payload;
  if (!_id) {
    throw new Error('Configuration ID is required');
  }

  const response = await api.put(
    `/v1/admin/configurations/${_id}`,
    body, // JSON body
  );

  return response.data;
}

export const useUpdateConfiguration = (
  _options?: Omit<
    UseMutationOptions<Configuration, Error, UpdateConfigurationPayload>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  const { showSnack } = useContext(SnackBarContext);

  return useMutation({
    mutationFn: updateConfiguration,
    onError: (_err) => {
      showSnack(
        'There was an error updating the configuration. Please try again',
        'error',
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configurationsKey });
      showSnack('Successfully updated the configuration', 'success');
    },
  });
};

async function deleteConfiguration(id: string) {
  const response = await api.delete(`/v1/admin/configurations/${id}`);
  return response.data;
}

export const useDeleteConfiguration = (
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteConfiguration,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: configurationsKey });
      options?.onSuccess?.(...args);
    },
  });
};
